import React, { useEffect, useId } from 'react';

import HipPractitionerPicker from './HipPractitionerPicker';
import {
  BANNER, PHASE, draftTitle, isSingleFile, kindText, needsDoctor, sizeText,
} from './hipUploadFiles';
import { HI_TYPE_ICONS, TYPE_DIAGNOSTIC, UPLOAD_LIMITS, hiTypeLabel } from '../../../constants/abdmHiTypes';
import { API_MODE, SLOT_SOURCE } from '../../../constants/hipRecordEnums';
import {
  INLINE, PRACTITIONER, REPLACE, ROWS, SLOT_STATE_LABEL, UPLOAD, slotName,
} from '../../../constants/hipRecordsCopy';
import { formatDateTimeISTWithYearNoSeconds } from '../../../utils/formatIST';

/**
 * HipInlineAddFile - the inline "Add file" / "Replace file" panel of an encounter card (OR-82, UX spec
 * ux-inline-add-file.md). It replaces the old upload drawer: it sits under the record-type strip, above the checklist.
 * It is NOT a checklist row: no tick box, never counted in "Link N records".
 *
 * Presentation only. The card owns the draft (one per card) and the requests, and hands in:
 *   draft   { mode: API_MODE.ADD | REPLACE, recordType, slotKey?, files: File[], title: string | null (null = untouched),
 *             practitionerId, banner: { kind, text } | null, phase: PHASE }
 *   slot    the slot a REPLACE points at (null for ADD)
 *   record  the EncounterRow (date for the default name, `needsPractitioner`)
 *   id / panelRef  the section's DOM id (the strip's aria-controls) and a ref the card focuses
 * and the handlers onTitle(text), onDoctor(memberId), onChooseFile(), onRemoveFile(index), onCancel(), onCommit().
 *
 * Shows (top to bottom): heading, ONE banner (error or "finish first"), the Replace block (Replace only), the files with
 * their ✕ (or the "Choose file" empty state), the name box (needs a file), "Doctor for patient" (ADD on a visit with no
 * doctor, OR-59), the outside-organisation line (ADD only, OR-16 / OR-11), then Cancel and the secondary teal button.
 * While a request runs (draft.phase != EDIT) every control is off and Esc does nothing.
 * Dark mode: Tailwind `dark:` pairs on solid surfaces (html.dark), no theme hook.
 */

// Solid fills in both modes (CLAUDE.md contrast + button rules).
const MUTED = 'text-xs text-slate-600 dark:text-slate-300';
const LABEL = 'mb-1 block text-xs font-bold text-slate-800 dark:text-slate-100';
const BTN = 'min-h-[44px] rounded-lg border border-slate-400 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-[40px] dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700';
const BTN_SM = 'rounded-lg border border-slate-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700';
const BTN_TEAL = 'inline-flex min-h-[44px] items-center justify-center rounded-lg border border-teal-500 bg-teal-50 px-4 py-2 text-sm font-bold text-teal-700 hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-[40px] dark:border-teal-400 dark:bg-teal-900 dark:text-teal-300 dark:hover:bg-teal-800';
const INPUT = 'w-full rounded-lg border border-slate-400 bg-white px-3 py-2 text-sm text-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100';
const PANEL = 'flex flex-col gap-2.5 scroll-mt-28 rounded-[10px] border-2 border-teal-500 bg-slate-50 p-3 outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:border-teal-400 dark:bg-slate-700';
const BANNER_TONE = {
  [BANNER.ERROR]: {
    box: 'border-red-600 bg-red-100 text-red-800 dark:border-red-400 dark:bg-red-900 dark:text-red-200',
    glyph: '!',
    glyphCls: 'bg-red-600 text-white dark:bg-red-300 dark:text-red-900',
    role: 'alert',
  },
  [BANNER.STATUS]: {
    box: 'border-amber-600 bg-yellow-100 text-yellow-800 dark:border-amber-400 dark:bg-yellow-900 dark:text-yellow-200',
    glyph: 'i',
    glyphCls: 'bg-amber-600 text-white dark:bg-amber-300 dark:text-amber-900',
    role: 'status',
  },
};

const when = (iso) => formatDateTimeISTWithYearNoSeconds(iso) || '—';

function Spinner() {
  return <span aria-hidden="true" className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />;
}

export default function HipInlineAddFile({
  id, panelRef, record, draft, slot, onTitle, onDoctor, onChooseFile, onRemoveFile, onCancel, onCommit,
}) {
  const uid = useId();
  const headingId = `${uid}-heading`;
  const nameId = `${uid}-name`;
  const nameHintId = `${uid}-name-hint`;
  const reasonId = `${uid}-reason`;

  const isReplace = draft.mode === API_MODE.REPLACE;
  const { recordType: type, files, banner } = draft;
  const typeLabel = hiTypeLabel(type);
  const single = isSingleFile(type);
  const busy = draft.phase !== PHASE.EDIT;
  const hasFiles = files.length > 0;
  const pickDoctor = needsDoctor(draft, record);
  const doctorMissing = pickDoctor && !draft.practitionerId;
  const reason = (() => {
    if (!hasFiles) return UPLOAD.fileReason;
    if (doctorMissing) return PRACTITIONER.pickReason;
    return null;
  })();
  const ctaLabel = (() => {
    if (draft.phase === PHASE.UPLOADING) return UPLOAD.ctaBusy;
    if (draft.phase === PHASE.LINKING) return SLOT_STATE_LABEL.LINKING;
    if (draft.phase === PHASE.REPLACING) return REPLACE.ctaBusy;
    return isReplace ? REPLACE.cta : UPLOAD.cta;
  })();
  const nameHelper = type === TYPE_DIAGNOSTIC ? UPLOAD.titleHelperDiagnostic : UPLOAD.nameHint;
  const tone = banner && BANNER_TONE[banner.kind];

  // A new panel takes focus (not the name box: that would raise the on-screen keyboard on a tablet) and scrolls into view.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    el.focus();
    const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [panelRef]);

  // Esc closes the panel - on the panel itself, never on document, so it never fights a modal. The doctor list
  // (a listbox, its trigger aria-expanded) closes first on its own Esc.
  const onKeyDown = (e) => {
    if (e.key !== 'Escape' || busy || e.defaultPrevented) return;
    if (e.target.closest && e.target.closest('[role="listbox"],[aria-expanded="true"]')) return;
    onCancel();
  };

  const hint = (
    <p className={`m-0 ${MUTED}`}>
      {single && <>{UPLOAD.singleFileHint}{' '}</>}
      {UPLOAD.fileRules}
    </p>
  );

  return (
    <section
      id={id} ref={panelRef} role="group" aria-labelledby={headingId} tabIndex={-1} aria-busy={busy || undefined}
      className={PANEL} data-testid="hip-inline-add-file" onKeyDown={onKeyDown}
    >
      <h4 id={headingId} className="m-0 text-[13.5px] font-bold text-slate-900 dark:text-slate-100">
        <span aria-hidden="true">{HI_TYPE_ICONS[type]}</span>
        {' '}
        {isReplace ? REPLACE.title(typeLabel) : UPLOAD.titleFor(typeLabel)}
      </h4>

      {tone && (
        <div role={tone.role} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold ${tone.box}`}>
          <span aria-hidden="true" className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full text-[11px] font-extrabold ${tone.glyphCls}`}>{tone.glyph}</span>
          <span className="min-w-0 [overflow-wrap:anywhere]">{banner.text}</span>
        </div>
      )}

      {isReplace && slot && (
        <div className="rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-[12.5px] text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
          <b className="block">{`${REPLACE.replacing}: ${slotName(slot)}`}</b>
          <span className={MUTED}>
            {slot.source === SLOT_SOURCE.STAFF
              ? `${ROWS.uploaded(when(slot.sourceAt)).trim()} · ${REPLACE.notLinkedYet}`
              : `${ROWS.savedInApp(when(slot.sourceAt))} · ${REPLACE.notLinkedYet}`}
          </span>
          {slot.source === SLOT_SOURCE.APP && <p className={`m-0 mt-1.5 ${MUTED}`}>{REPLACE.body}</p>}
          {slot.fileCount > 1 && <p className="m-0 mt-1.5 text-xs font-bold">{REPLACE.replacesAll(slot.fileCount)}</p>}
        </div>
      )}

      {hasFiles ? (
        <div className="flex flex-col gap-1.5" data-testid="hip-inline-files">
          {files.map((f, i) => (
            // Index in the key: the same file can be picked twice; the chip holds no state.
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${i}:${f.name}`} className="flex items-center gap-2.5 rounded-[10px] border border-emerald-500 bg-emerald-50 px-3 py-2 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100">
              <span aria-hidden="true" className="text-xl">{HI_TYPE_ICONS.HealthDocumentRecord}</span>
              <div className="min-w-0 flex-1">
                <b className="block text-[12.5px] [overflow-wrap:anywhere]">{f.name}</b>
                <small>{kindText(f.name)} · {sizeText(f.size)}</small>
              </div>
              <button
                type="button" disabled={busy} aria-label={INLINE.removeFile(f.name)} title={INLINE.removeFile(f.name)}
                className="h-9 w-9 flex-none rounded-lg border border-slate-400 bg-white text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                onClick={() => onRemoveFile(i)}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          ))}
          {!single && files.length < UPLOAD_LIMITS.filesPerUpload && (
            <button type="button" className={`${BTN_SM} self-start`} disabled={busy} onClick={onChooseFile}>{UPLOAD.chooseAnother}</button>
          )}
          {hint}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-[10px] border-2 border-dashed border-slate-400 bg-white p-3.5 text-center dark:border-slate-500 dark:bg-slate-800" data-testid="hip-inline-empty">
          <button type="button" className={BTN_SM} disabled={busy} onClick={onChooseFile}>{UPLOAD.chooseFileButton}</button>
          {hint}
        </div>
      )}

      {hasFiles && (
        <div>
          <label htmlFor={nameId} className={LABEL}>{UPLOAD.stepName}</label>
          <input
            id={nameId} className={INPUT} type="text" maxLength={100} autoComplete="off" disabled={busy}
            value={draftTitle(draft, record)} aria-describedby={nameHintId} onChange={(e) => onTitle(e.target.value)}
          />
          <p id={nameHintId} className={`m-0 mt-1 ${MUTED}`}>{nameHelper}</p>
        </div>
      )}

      {hasFiles && pickDoctor && (
        // Stays editable until the request starts: the pick is part of the link.
        <div className="[&_[data-testid=hip-practitioner-picker]]:mb-0" data-testid="hip-inline-doctor">
          <span className={LABEL}>{UPLOAD.stepDoctor}</span>
          <fieldset disabled={busy} className="m-0 min-w-0 border-0 p-0">
            <HipPractitionerPicker value={draft.practitionerId} onChange={onDoctor} showReason={false} showLabel={false} />
          </fieldset>
        </div>
      )}

      {!isReplace && <p className={`m-0 ${MUTED}`}>{UPLOAD.outsideGuidance}</p>}

      <div className="flex flex-col gap-2">
        {reason && !busy && <p id={reasonId} className="m-0 text-xs font-semibold text-slate-700 dark:text-slate-300">{reason}</p>}
        {isReplace && <p className={`m-0 ${MUTED}`}>{REPLACE.footerNote}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button type="button" className={BTN} disabled={busy} onClick={onCancel}>{UPLOAD.cancel}</button>
          <button
            type="button" className={BTN_TEAL} disabled={busy || !!reason}
            aria-describedby={reason && !busy ? reasonId : undefined} onClick={onCommit}
          >
            {busy && <Spinner />}
            {ctaLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
